import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound, Shield, Users as UsersIcon } from "lucide-react";
import BBSLogo from "@/components/BBSLogo";
import { Button } from "@/components/ui/button";

/**
 * Forgot Password screen.
 *
 * Asset IQ is an internal-only app for BBS Global — password recovery is
 * handled directly by an Admin via the User Management page (no email link
 * required). This page tells the user exactly what to do.
 */
export default function ForgotPassword() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6 py-12">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <BBSLogo size={54} />

        <div className="mt-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fcf0f3] text-[#7C1D3A] text-xs font-semibold tracking-wider uppercase mb-4">
            <KeyRound className="w-3 h-3" />
            Password Recovery
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            Forgot your <span className="text-[#7C1D3A]">password?</span>
          </h1>
          <p className="mt-3 text-slate-600 text-sm leading-relaxed">
            Asset IQ is an internal system, so password recovery is handled by
            your BBS Global administrator — not by email.
          </p>
        </div>

        <div className="mt-8 border border-slate-200 rounded-lg divide-y divide-slate-100">
          <Step
            n={1}
            icon={UsersIcon}
            title="Reach out to your admin"
            body="Contact any user with the Admin role at BBS Global — usually IT / Systems or the Managing Director's office."
          />
          <Step
            n={2}
            icon={Shield}
            title="Ask them to reset your password"
            body="From the User Management screen, your admin can generate a fresh temporary password for your account in a few seconds."
          />
          <Step
            n={3}
            icon={KeyRound}
            title="Sign in and change it"
            body="Use the temporary password to sign in, then update it right away from the profile menu (bottom-left) → Change password."
          />
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign-in
          </Link>
          <Button asChild variant="outline" size="sm">
            <a href="mailto:it@bbsglobal.in?subject=Asset%20IQ%20-%20Password%20reset%20request">
              Email IT team
            </a>
          </Button>
        </div>

        <div className="mt-10 text-[10px] font-mono text-slate-400 text-center border-t border-slate-100 pt-4">
          © BBS Global Pvt. Ltd. · Asset IQ · Internal Use Only
        </div>
      </div>
    </div>
  );
}

function Step({ n, icon: Icon, title, body }) {
  return (
    <div className="flex gap-4 p-4">
      <div className="w-9 h-9 rounded-full bg-[#fcf0f3] text-[#7C1D3A] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-1">
          Step {n}
        </div>
        <div className="font-heading font-semibold text-slate-900">{title}</div>
        <div className="text-sm text-slate-600 mt-0.5 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
